import type { DependencyList } from 'react';
import { useEditor, type Editor, type UseEditorOptions } from '@tiptap/react';

/**
 * Options for a rich-text composer. `immediatelyRender` is deliberately not
 * accepted: this hook exists to pin it to `false` (see below).
 */
export type RichTextEditorOptions = Omit<UseEditorOptions, 'immediatelyRender'>;

/**
 * The platform's one way to create a TipTap editor in a React component.
 *
 * TipTap's `useEditor` defaults to creating the editor DURING RENDER and arming
 * a ~1 ms timer that destroys it unless the component's mount effect runs
 * first. That gap is normally microseconds, but a time-sliced render (the
 * Suspense retry that mounts a lazy composer chunk on a cold page load) or a
 * stalled main thread (Firefox's cycle collector, 2026-10-15) lets the timer
 * win, and the component is handed an instance that is already dead. Our own
 * reads were guarded on 2026-09-29; anything else that touched the instance in
 * that window — TipTap itself, React's concurrent recovery — still crashed the
 * chat page into the route boundary on reload.
 *
 * `immediatelyRender: false` removes the race rather than guarding it: the
 * editor is created in the mount effect, so there is no destroy timer and
 * nothing to beat. The cost is that the FIRST render has no editor — callers
 * must treat `null` as "not ready yet", which every composer already does.
 *
 * Pinned by `src/test/architecture/rich-text-editor-mount.test.ts`: `useEditor`
 * may not be imported anywhere else in the client.
 */
export function useRichTextEditor(
  options: RichTextEditorOptions,
  deps?: DependencyList,
): Editor | null {
  return useEditor({ ...options, immediatelyRender: false }, deps);
}
